/**
 * <s-signup-popup> — timed email signup popup
 * Opens its <dialog> once after a delay, then remembers the shopper's choice.
 *
 * Attributes:
 *   delay        — milliseconds before the popup opens (default 5000)
 *   suppress-days — days to stay hidden after a shopper closes it (default 14)
 *   open-now     — open immediately (server-returned success or error state)
 *   subscribed   — mark the shopper as signed up so it never opens again
 *   auto-open    — allow the timed open (omitted in the theme editor)
 */
const STORAGE_KEY = 'chimp-signup-popup';

class SSignupPopup extends HTMLElement {
  #dialog;
  #timer;
  #ac;

  connectedCallback() {
    this.#dialog = this.querySelector('dialog');
    if (!this.#dialog) return;

    this.#ac = new AbortController();

    if (this.hasAttribute('subscribed')) {
      // Only show the success state on the first load after signing up: a refresh
      // or a revisit of this URL should not reopen it.
      const alreadyShown = this.#stored() === 'subscribed';
      this.#remember('subscribed');
      this.#clearPostedFlag();
      if (alreadyShown) return;
    }

    this.#dialog.addEventListener('close', () => {
      if (!this.hasAttribute('subscribed')) this.#remember(`dismissed:${Date.now()}`);
    }, { signal: this.#ac.signal });

    if (this.hasAttribute('open-now')) {
      this.#open();
      return;
    }

    if (!this.hasAttribute('auto-open') || this.#suppressed()) return;

    const delay = Number(this.getAttribute('delay')) || 5000;
    this.#timer = window.setTimeout(() => this.#open(), delay);
  }

  disconnectedCallback() {
    window.clearTimeout(this.#timer);
    this.#ac?.abort();
  }

  #open() {
    if (this.#dialog.open) return;
    // Wait if another modal (cart, search, menu) is already open.
    if (document.querySelector('dialog:modal')) {
      this.#timer = window.setTimeout(() => this.#open(), 3000);
      return;
    }
    this.#dialog.showModal();
  }

  #stored() {
    try {
      return window.localStorage.getItem(STORAGE_KEY);
    } catch {
      return null;
    }
  }

  #clearPostedFlag() {
    const url = new URL(window.location.href);
    if (!url.searchParams.has('customer_posted')) return;
    url.searchParams.delete('customer_posted');
    url.hash = '';
    window.history.replaceState(window.history.state, '', url);
  }

  #suppressed() {
    const value = this.#stored();
    if (!value) return false;
    if (value === 'subscribed') return true;

    const dismissedAt = Number(value.split(':')[1]);
    const days = Number(this.getAttribute('suppress-days')) || 14;
    return Number.isFinite(dismissedAt) && Date.now() - dismissedAt < days * 86400000;
  }

  #remember(value) {
    try {
      window.localStorage.setItem(STORAGE_KEY, value);
    } catch {
      /* Storage can be unavailable in private browsing; the popup then simply reappears later. */
    }
  }
}

if (!customElements.get('s-signup-popup')) {
  customElements.define('s-signup-popup', SSignupPopup);
}
